function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { useCallback, useEffect, useRef } from 'react';
import { getSize, padFirstAndLastRow, TableUtils } from '@deephaven/jsapi-utils';
import { useApi } from '@deephaven/jsapi-bootstrap';
import useTableListener from "./useTableListener.js"; // Stable no-op used as the useTableListener callback when onTableUpdated is null
var noopUpdatedHandler = _event => undefined;

/**
 * Creates a callback function that will set a Table viewport. The callback has
 * a closure over the Table, a desired viewport size, and additional padding.
 * These will be combined with a first row index passed to the callback to
 * calculate the final viewport.
 * @param table The `Table` or `TreeTable` to retrieve data from.
 * @param viewportSize The desired viewport size.
 * @param viewportPadding The padding to add before and after the viewport.
 * @param viewportSubscriptionOptions The viewport subscription options to use. If provided and
 * the table is not a `TreeTable`, the data will be requested using a `TableViewportSubscription`.
 * Rows and columns are filled in when the subscription is created if they are missing.
 * @param onUpdated Optional handler for TABLE_UPDATED events. When provided, it is
 * registered on the table or directly on the subscription
 * @returns A callback function for setting the viewport.
 */
export function useSetPaddedViewportCallback(table, viewportSize, viewportPadding) {
  var viewportSubscriptionOptions = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  var onUpdated = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : noopUpdatedHandler;
  var dh = useApi();
  var subscriptionRef = useRef(null);
  var removeSubscriptionListenerRef = useRef(null);
  var prevTableRef = useRef(null);
  var prevViewportOptionsRef = useRef(null);
  var cleanupSubscription = () => {
    var _removeSubscriptionLi;
    (_removeSubscriptionLi = removeSubscriptionListenerRef.current) === null || _removeSubscriptionLi === void 0 || _removeSubscriptionLi.call(removeSubscriptionListenerRef);
    removeSubscriptionListenerRef.current = null;
    if (subscriptionRef.current) {
      subscriptionRef.current.close();
      subscriptionRef.current = null;
    }
  };
  if (prevTableRef.current !== table || prevViewportOptionsRef.current !== viewportSubscriptionOptions) {
    prevTableRef.current = table;
    prevViewportOptionsRef.current = viewportSubscriptionOptions;
    cleanupSubscription();
  }
  useEffect(() => cleanupSubscription, []);

  // Only use a subscription when options are provided AND the table is not a
  // TreeTable (which does not support createViewportSubscription).
  var needsSubscription = viewportSubscriptionOptions != null && table != null && !TableUtils.isTreeTable(table);

  // For the setViewport path (including TreeTable + options), register
  // TABLE_UPDATED on the table directly.
  // For the subscription path the listener is registered on the subscription
  // inside setPaddedViewport when it is first created.
  useTableListener(needsSubscription ? null : table, dh.Table.EVENT_UPDATED, onUpdated);
  return useCallback(function setPaddedViewport(firstRow) {
    if (table == null) {
      return;
    }
    var _padFirstAndLastRow = padFirstAndLastRow(firstRow, viewportSize, viewportPadding, getSize(table)),
      _padFirstAndLastRow2 = _slicedToArray(_padFirstAndLastRow, 2),
      first = _padFirstAndLastRow2[0],
      last = _padFirstAndLastRow2[1];
    if (subscriptionRef.current == null && needsSubscription) {
      var _viewportSubscription, _viewportSubscription2;
      var subscriptionOptions = _objectSpread(_objectSpread({}, viewportSubscriptionOptions), {}, {
        rows: (_viewportSubscription = viewportSubscriptionOptions.rows) !== null && _viewportSubscription !== void 0 ? _viewportSubscription : {
          first,
          last
        },
        columns: (_viewportSubscription2 = viewportSubscriptionOptions.columns) !== null && _viewportSubscription2 !== void 0 ? _viewportSubscription2 : table.columns
      });
      subscriptionRef.current = table.createViewportSubscription(subscriptionOptions);

      // TABLE_UPDATED fires on the subscription rather than
      // the table. Register the listener directly on the subscription.
      removeSubscriptionListenerRef.current = subscriptionRef.current.addEventListener(dh.Table.EVENT_UPDATED, onUpdated);
    }
    if (subscriptionRef.current == null) {
      table.setViewport(first, last);
      return;
    }
    subscriptionRef.current.update({
      rows: {
        first,
        last
      },
      columns: table.columns
    });
  }, [table, viewportPadding, viewportSize, viewportSubscriptionOptions, needsSubscription, onUpdated, dh.Table.EVENT_UPDATED]);
}
export default useSetPaddedViewportCallback;
//# sourceMappingURL=useSetPaddedViewportCallback.js.map